import React, { useEffect, useState } from "react";
import {
  GoATable,
  GoABadge,
  GoAButton,
  GoASpacer,
  GoAContainer,
  GoAModal,
  GoAButtonGroup,
} from "@abgov/react-components";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../state/store";
import useCsrService from "../services/csr-api-redux";
import { fetchCollisionsByEmail, deleteCollisionById } from "../state/slices/collisionSlice";
import { useDispatch } from "react-redux";
import { getLoginInfoFromKeycloakJwt, KeycloakJwtPayload } from "../state/slices/authSlice";
import {
  updateCollision,
  clearCollision,
} from "../state/slices/collisionSlice";

const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected
) => TSelected = useSelector;

const useAppDispatch = () => useDispatch<AppDispatch>();

export const DeveloperAdminComponent = () => {
  const dispatch = useAppDispatch();
  const csrService = useCsrService();
  const stateAuth = useAppSelector((state: RootState) => state.auth);
  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const [reports, setReports] = useState<
    { id: number; status: string; collisionType: number, updatedAt: string }[]
  >([]);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [userInfoFromToken, setUserInfoFromToken] = useState<KeycloakJwtPayload | null>(
    () => (stateAuth.auth.access?.access ? getLoginInfoFromKeycloakJwt(stateAuth.auth.access.access) : null)
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (userInfoFromToken?.email) {
      dispatch(
        fetchCollisionsByEmail({
          username: userInfoFromToken?.email,
          csrService,
          reportStatus: [1, 2, 3], // Fetch reports in all statuses
          lastUpdatedWithinDays: 60,
        })
      )
        .unwrap()
        .then(({ status, data }) => {
          if (status === 200) {
            const formattedReports = data.map((report: any) => ({
              id: report.id,
              status: getReportStatus(report.reportStatus),
              collisionType: report.collisionType ?? "Unknown",
              updatedAt: report.updatedAt,
            }));
            setReports(formattedReports);
          }
        })
        .catch((error) => {
          console.error("Error fetching reports:", error);
        });
    }
  }, [userInfoFromToken]);

  const getReportStatus = (statusCode: number): string => {
    switch (statusCode) {
      case 1:
        return "In Progress";
      case 2:
        return "Submitted";
      case 3:
        return "Under Review";
      case 4:
        return "Approved";
      case 5:
        return "Rejected";
      default:
        return "Unknown";
    }
  };

  const handleDeleteReport = async () => {
    if (!selectedReportId) return;

    try {
      dispatch(clearCollision());
      await dispatch(deleteCollisionById(
        {
          id: selectedReportId.toString(),
          csrService
        })
      ).unwrap();
      setReports((prevReports) => prevReports.filter((report) => report.id !== selectedReportId));
      console.log(`Successfully deleted report ID: ${selectedReportId}`);
      dispatch(updateCollision(stateCollision.collision));

    } catch (error) {
      console.error("Failed to delete report:", error);
    }
    finally {
      setOpen(false);
    }
  };

  return (
    <GoAContainer accent="thick" width="full">
      <h2>Developer Admin - Collision Reports</h2>
      <GoASpacer vSpacing="m" />
      <GoATable width="100%">
        <thead>
          <tr>
            <th>Status</th>
            <th>Collision Type</th>
            <th>Report ID</th>
            <th>Last Updated on</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {reports.length > 0 ? (
            reports.map((report) => (
              <tr key={report.id}>
                <td>
                  <GoABadge type="information" content={report.status} />
                </td>
                <td>{report.collisionType}</td>
                <td>{report.id}</td>
                <td>{report.updatedAt}</td>
                <td>
                  <GoAButton onClick={() => {
                    setSelectedReportId(report.id);
                    setOpen(true);
                  }}>
                    Delete Report
                  </GoAButton>
                  <GoAModal
                    heading="Are you sure you want to delete this report?"
                    role="alertdialog"
                    open={open}
                    onClose={() => {
                      setOpen(false);
                      setSelectedReportId(null);
                    }} >
                    <GoAButtonGroup alignment="end" mt="xl">
                      <GoAButton type="primary" testId={report.id.toString()} onClick={() => handleDeleteReport()}>
                        Yes
                      </GoAButton>
                      <GoAButton type="secondary" onClick={() => {
                        setSelectedReportId(null);
                        setOpen(false);
                      }}>
                        No
                      </GoAButton>
                    </GoAButtonGroup>
                  </GoAModal>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} style={{ textAlign: "center" }}>
                No reports found
              </td>
            </tr>
          )}
        </tbody>
      </GoATable>
    </GoAContainer>
  );
};
